import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  Stack,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery,
  Link,
  IconButton,
  TextField,
  Button
} from '@mui/material';
import {
  Newspaper,
  Public,
  TrendingUp,
  Verified,
  LocationOn,
  AccessTime,
  Group,
  EmojiEvents,
  Phone,
  Email,
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  LinkedIn
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const stats = [
    { icon: <Newspaper />, label: "Daily Articles", value: "50+" },
    { icon: <Public />, label: "Countries Covered", value: "10+" },
    { icon: <TrendingUp />, label: "Monthly Readers", value: "100K+" },
    { icon: <Verified />, label: "Fact-Checked Stories", value: "100%" }
  ];

  const values = [
    {
      icon: <Verified color="primary" />,
      title: "Truth & Accuracy",
      description: "We are committed to delivering verified, fact-checked news that our readers can trust."
    },
    {
      icon: <AccessTime color="primary" />,
      title: "Timeliness",
      description: "Providing breaking news and updates as they happen across Ethiopia and East Africa."
    },
    {
      icon: <Group color="primary" />,
      title: "Community Focus",
      description: "Serving the interests and needs of our local and regional communities."
    },
    {
      icon: <EmojiEvents color="primary" />,
      title: "Excellence",
      description: "Maintaining the highest standards of journalism and editorial quality."
    }
  ];

  const coverageAreas = [
    "Ethiopia",
    "Kenya",
    "Tanzania",
    "Uganda",
    "Rwanda",
    "Burundi",
    "South Sudan",
    "Somalia",
    "Djibouti",
    "Eritrea"
  ];

  return (
    <>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 8,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              component="h1"
              align="center"
              sx={{
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' }
              }}
            >
              About EthioReview
            </Typography>
            <Typography
              variant="h5"
              align="center"
              sx={{
                opacity: 0.9,
                maxWidth: '800px',
                mx: 'auto',
                mb: 4
              }}
            >
              Your trusted source for truthful, comprehensive news coverage of Ethiopia and East Africa
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              mb: 6,
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}
            >
              Our Mission
            </Typography>
            <Typography variant="h6" paragraph sx={{ lineHeight: 1.8 }}>
              EthioReview is dedicated to providing accurate, timely, and comprehensive news coverage 
              that serves the people of Ethiopia and the broader East African region. We believe in 
              the power of truthful journalism to inform, educate, and empower our communities.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              Our commitment extends beyond just reporting the news – we strive to provide context, 
              analysis, and perspectives that help our readers understand the complex issues facing 
              our region. From politics and business to sports and culture, we cover the stories 
              that matter most to our audience.
            </Typography>
          </Paper>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {stats.map((stat, index) => (
              <Grid xs={6} md={3} key={index}>
                <Card
                  component={motion.div}
                  whileHover={{ y: -5 }}
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    height: '100%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.main}10 100%)`
                  }}
                >
                  <CardContent>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Values Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 'bold', color: 'primary.main', mb: 4, textAlign: 'center' }}
          >
            Our Values
          </Typography>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {values.map((value, index) => (
              <Grid xs={12} md={6} key={index}>
                <Card
                  component={motion.div}
                  whileHover={{ y: -5, scale: 1.02 }}
                  sx={{
                    height: '100%',
                    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box sx={{ 
                      mb: 3,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.primary.main}20 100%)`,
                      border: `2px solid ${theme.palette.primary.light}`
                    }}>
                      {React.cloneElement(value.icon, { 
                        sx: { fontSize: 28, color: theme.palette.primary.main } 
                      })}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Coverage Areas */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}
            >
              Our Coverage
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              EthioReview provides comprehensive coverage across East Africa, with a particular focus 
              on Ethiopia. Our dedicated team of journalists and correspondents work tirelessly to 
              bring you the latest news from across the region.
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Countries We Cover:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {coverageAreas.map((country, index) => (
                  <Chip
                    key={index}
                    label={country}
                    icon={<LocationOn />}
                    variant="outlined"
                    color="primary"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            </Box>

            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              From breaking political developments to business insights, sports coverage, and cultural 
              stories, we ensure our readers stay informed about all aspects of life in Ethiopia and 
              East Africa. Our commitment to truth and accuracy makes us a reliable source for 
              understanding the complex dynamics of our region.
            </Typography>
          </Paper>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white'
            }}
          >
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Stay Connected
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Join thousands of readers who trust EthioReview for their daily news. 
              Stay informed about Ethiopia and East Africa with accurate, timely, and comprehensive coverage.
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Your trusted source for truthful news about Ethiopia and East Africa
            </Typography>
          </Paper>
        </motion.div>
             </Container>
     </Box>
     
     {/* Footer */}
     <Box
       component="footer"
       sx={{
         py: 4,
         px: 2,
         backgroundColor: "background.paper",
         borderTop: 1,
         borderColor: "divider",
       }}
     >
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        {/* Quick Links */}
        <Grid xs={12} sm={6} md={3}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
            Quick Links
          </Typography>
                         <Stack spacing={1}>
             {[
               { name: "Home", path: "/" },
               { name: "About Us", path: "/about" },
               { name: "Contact", path: "/contact" }
             ].map((item) => (
               <motion.div whileHover={{ x: 5 }} key={item.name}>
                 <Link
                   to={item.path}
                   style={{
                     display: "block",
                     color: "inherit",
                     textDecoration: "none",
                   }}
                   sx={{
                     color: "text.secondary",
                     "&:hover": { color: "primary.main" },
                   }}
                 >
                   {item.name}
                 </Link>
               </motion.div>
             ))}
          </Stack>
        </Grid>

        {/* Categories */}
        <Grid xs={12} sm={6} md={3}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
            Categories
          </Typography>
          <Stack spacing={1}>
            {["Politics", "Business", "Sports"].map((category) => (
              <motion.div whileHover={{ x: 5 }} key={category}>
                                 <Link
                   to={`/${category.toLowerCase()}-news`}
                   style={{
                     display: "block",
                     color: "inherit",
                     textDecoration: "none",
                   }}
                   sx={{
                     color: "text.secondary",
                     "&:hover": { color: "primary.main" },
                   }}
                 >
                   {category}
                 </Link>
              </motion.div>
            ))}
          </Stack>
        </Grid>

        {/* Contact Info */}
        <Grid xs={12} sm={6} md={3}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
            Contact Us
          </Typography>
          <Stack spacing={1.5}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LocationOn color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2">
                123 News Street, Addis Ababa, Ethiopia
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Phone color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2">+251 123 456 789</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Email color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2">info@ethioreview.com</Typography>
            </Box>
          </Stack>
        </Grid>

        {/* Social Media & Newsletter */}
        <Grid xs={12} sm={6} md={3}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
            Follow Us
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            {[
              { icon: <Facebook />, color: "#3b5998", name: "Facebook" },
              { icon: <Twitter />, color: "#1DA1F2", name: "Twitter" },
              { icon: <Instagram />, color: "#E1306C", name: "Instagram" },
              { icon: <YouTube />, color: "#FF0000", name: "YouTube" },
              { icon: <LinkedIn />, color: "#0077B5", name: "LinkedIn" },
            ].map((social) => (
              <motion.div whileHover={{ scale: 1.1 }} key={social.name}>
                <IconButton
                  href="#"
                  target="_blank"
                  rel="noopener"
                  sx={{
                    backgroundColor: `${social.color}10`,
                    color: social.color,
                    "&:hover": { backgroundColor: `${social.color}20` },
                  }}
                >
                  {social.icon}
                </IconButton>
              </motion.div>
            ))}
          </Stack>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
            Newsletter
          </Typography>
          <Box component="form" sx={{ display: "flex" }}>
            <TextField
              size="small"
              placeholder="Your email"
              variant="outlined"
              fullWidth
              sx={{ mr: 1 }}
            />
            <Button
              variant="contained"
              color="primary"
              sx={{ whiteSpace: "nowrap" }}
            >
              Subscribe
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Copyright & Legal */}
      <Box
        sx={{
          mt: 4,
          pt: 3,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} EthioReview. All rights reserved.
        </Typography>

                 <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
           {["Privacy Policy", "Terms of Service", "Cookie Policy"].map(
             (item) => (
               <Typography
                 key={item}
                 component={Link}
                 href="#"
                 variant="body2"
                 sx={{
                   color: "text.secondary",
                   textDecoration: "none",
                   "&:hover": { color: "primary.main" },
                 }}
               >
                 {item}
               </Typography>
             )
           )}
         </Stack>
       </Box>
     </Container>
   </Box>
   </>
   );
 };

 export default About;
